import { Fragment, type ReactNode } from "react";
import { Link } from "react-router-dom";

interface ArticleHeroProps {
  title: string;
  excerpt: string;
  category: string;

  categorySlug: string;
  type: string;
  heroImage: string;
  author: string;
  authorAvatar: string;
  date: string;
  updatedDate: string;
  readTime: string;
  views: number;
  tags: string[];
}

function MetaDivider() {
  return <span className="h-2 w-2 rounded-full bg-white/55"></span>;
}

function MetaText({ icon, children }: { icon: string; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-white/85">
      <i className={`${icon} text-sm text-white/70`}></i>
      <span>{children}</span>
    </span>
  );
}

export default function ArticleHero({
  title,
  excerpt,
  category,
  categorySlug,
  type,
  heroImage,
  author,
  authorAvatar,
  date,
  updatedDate,
  readTime,
  views,
  tags,
}: ArticleHeroProps) {
  return (
    <div className="relative overflow-hidden bg-black">
      <div className="absolute inset-0">
        <img
          src={heroImage}
          alt={title}
          className="h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30"></div>
      </div>

      <div className="relative mx-auto flex min-h-[280px] w-full max-w-7xl flex-col justify-end px-4 pb-4 pt-12 md:min-h-[420px] md:px-6 md:pb-6">
        <div className="max-w-4xl">
          <div className="mb-3 flex items-center gap-2">
            <Link
              to={`/categoria/${categorySlug}`}
              className="rounded-full bg-orange-500 px-3 py-1 text-[11px] font-bold whitespace-nowrap text-white transition-colors hover:bg-orange-600"
            >
              {category}
            </Link>
            <span className="text-[11px] text-white/70">{type}</span>
          </div>

          <h1 className="mb-3 text-xl font-black leading-tight text-white md:text-3xl">
            {title}
          </h1>

          <p className="max-w-3xl text-sm leading-relaxed text-white/85 md:text-base">
            {excerpt}
          </p>
        </div>

        <div className="mt-6 pb-1">
          <div className="flex max-w-full flex-wrap items-center gap-x-3 gap-y-2 rounded-3xl border border-white/15 bg-black/25 px-4 py-2.5 backdrop-blur-md">
            <span className="inline-flex items-center gap-2 text-xs text-white/90">

              <span className="h-8 w-8 overflow-hidden rounded-full border border-white/20">
                <img src={authorAvatar} alt={author} className="h-full w-full object-cover object-top" />
              </span>
              <span className="font-semibold">{author}</span>
            </span>

            <MetaDivider />
            <MetaText icon="ri-eye-line">{views.toLocaleString("pt-BR")}</MetaText>
            <MetaDivider />
            <MetaText icon="ri-time-line">{readTime} de leitura</MetaText>

            {tags.slice(0, 4).map((tag) => (
              <Fragment key={tag}>
                <MetaDivider />
                <span className="text-xs text-white/85">
                  #{tag}
                </span>

              </Fragment>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}